import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cpvMatches, withoutNestedCpv } from '@tenders/shared';
import { useLocale } from '../i18n/locale-context';
import { getCompanyCodes, type AiIkpuRef } from '../lib/ai-search-client';
import { updateSavedSearch, type SavedSearch } from '../lib/saved-search-client';
import { SAVED_SEARCH_CODES_MAX } from '../lib/saved-search-limits';
import { midSentence } from './company-payoff';

/** The codes a search already carries — the stored blob first, the legacy
 *  scalar for a row saved before the blob had an `ikpu` list. */
export function searchIkpuCodes(search: SavedSearch): string[] {
  const fromBlob = search.criteria?.include?.ikpu;
  if (fromBlob && fromBlob.length > 0) return fromBlob;
  return search.ikpuCode ? [search.ikpuCode] : [];
}

/** How many code names the line spells out before «and N more». */
const NAMED = 2;

/** Is `code` strictly under `parent` in the CPV tree? Never for a UZ/KZ code. */
function under(code: string, parent: string): boolean {
  return code !== parent && cpvMatches(code, parent);
}

/**
 * What the button would write: the search's own codes, deduplicated, then the
 * company's codes it does not carry yet — in the order the API ranks them, by
 * how many awards each covers — up to the API's cap of
 * {@link SAVED_SEARCH_CODES_MAX}. Negative QA 22.10, Н2: a 4-code AI search plus
 * 5 award codes was sent as 9, refused with 400 «ikpuCodes must contain no more
 * than 8 elements» and shown as «No se han podido añadir», on every retry.
 *
 * Nested codes (CPV check 22.10): a code under one the search already holds,
 * or under another offered code, is not offered — the broader code already
 * brings its lots, and the nested one only spent a slot of the eight and
 * promised more than would arrive. An offered code that is broader than one the
 * search holds REPLACES it (the union is unchanged, a slot is freed) — but only
 * once that code actually fits, so a code never leaves without its parent.
 */
export function mergeCompanyCodes(
  have: readonly string[],
  offered: readonly AiIkpuRef[],
): { codes: string[]; fresh: AiIkpuRef[]; added: AiIkpuRef[] } {
  const own = [...new Set(have)];
  const candidates = withoutNestedCpv(offered, (c) => c.code);
  const fresh = candidates.filter((c) => !own.some((h) => h === c.code || under(c.code, h)));
  let codes = own;
  const added: AiIkpuRef[] = [];
  for (const c of fresh) {
    const kept = codes.filter((h) => !under(h, c.code));
    if (kept.length + 1 > SAVED_SEARCH_CODES_MAX) continue;
    codes = [...kept, c.code];
    added.push(c);
  }
  return { codes, fresh, added };
}

/**
 * «servicios de limpieza, limpieza de edificios y 3 más» — the first two
 * names and a count, joined the way the locale joins a list. Spain only renders
 * this, and every browser ships Spanish list data; a runtime without
 * `Intl.ListFormat` gets commas.
 */
function namesLine(
  codes: readonly AiIkpuRef[],
  locale: string,
  more: (n: number) => string,
): string {
  const items = codes.slice(0, NAMED).map((c) => midSentence(c.name || c.code));
  if (codes.length > NAMED) items.push(more(codes.length - NAMED));
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/**
 * The company step's offer (Spain, `features.aiCompanyStep`, owner 2026-10-22):
 * once the NIF is saved and has awards, ONE line naming the CPV codes of those
 * awards and ONE button that adds them to the search the reader just saved.
 *
 * It used to be the «o NIF» door of «¿Qué ofreces?», which turned the codes
 * into the proposal without asking. Here nothing changes until the button is
 * pressed. The codes come from `GET /ai-search/company-codes` — no model call,
 * no AI-search attempt spent — and join the search's SUBJECT (a union with its
 * categories and codes); its words, regions and budget still narrow it.
 *
 * Renders nothing while the codes load, when there are none, when the search
 * already carries all of them, or when the request fails: an offer that cannot
 * be made is simply not made — the payoff above it already said what we know.
 */
export function CompanyCodesOffer({
  target,
  onAdded,
}: {
  /** The search the codes would join. */
  target: SavedSearch;
  onAdded: (updated: SavedSearch) => void;
}) {
  const { t, locale } = useLocale();
  const [state, setState] = useState<'idle' | 'saving' | 'added' | 'error'>('idle');
  /** How many of how many went in, when the cap left some out. */
  const [cut, setCut] = useState<{ added: number; total: number } | null>(null);
  const { data } = useQuery({
    queryKey: ['ai-company-codes'],
    queryFn: getCompanyCodes,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (state === 'added') {
    return (
      <p
        role="status"
        className="mb-4 flex items-center gap-1.5 text-[13px] font-semibold text-brand"
      >
        <span aria-hidden>✓</span>
        {cut
          ? t('onboarding.companyCodes.addedSome', {
              added: cut.added,
              total: cut.total,
              max: SAVED_SEARCH_CODES_MAX,
            })
          : t('onboarding.companyCodes.added')}
      </p>
    );
  }

  const merge = mergeCompanyCodes(searchIkpuCodes(target), data?.codes ?? []);
  const { fresh, added } = merge;
  // Nothing new, or a search already at the cap: a button that cannot add
  // anything is not offered.
  if (added.length === 0) return null;

  async function add() {
    setState('saving');
    try {
      const updated = await updateSavedSearch(target.id, { ikpuCodes: merge.codes });
      setCut(added.length < fresh.length ? { added: added.length, total: fresh.length } : null);
      setState('added');
      onAdded(updated);
    } catch {
      setState('error');
    }
  }

  return (
    <div
      className="mb-4 rounded-lg border border-edge bg-bone p-3"
      data-testid="company-codes-offer"
    >
      <p className="text-[13px] leading-[1.55] text-ink">
        {t('onboarding.companyCodes.offer', {
          names: namesLine(fresh, locale, (n) => t('onboarding.companyCodes.more', { n })),
        })}
      </p>
      <button
        type="button"
        onClick={() => void add()}
        disabled={state === 'saving'}
        className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-md border-[1.5px] border-brand bg-bone-surface px-4 text-[13px] font-bold text-brand transition-colors hover:bg-brand-dim disabled:pointer-events-none disabled:opacity-60"
      >
        {state === 'saving'
          ? t('onboarding.companyCodes.adding')
          : t('onboarding.companyCodes.add')}
      </button>
      {state === 'error' && (
        <p className="mt-2 text-[12.5px] text-status-closing-text" role="alert">
          {t('onboarding.companyCodes.error')}
        </p>
      )}
    </div>
  );
}
